import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import api from '../../system/client/src/services/api';
import './OnboardingTour.css';

/* Steps are defined declaratively. Each step is one of:
 *
 *   { kind: 'modal',     route?, kicker, title, body, primary, secondary? }
 *   { kind: 'spotlight', route?, selector, kicker, title, body,
 *                        primary?, before?, autoAdvance? }
 *
 * `route` — if set, the tour navigates here on entry. Used to anchor the
 *           step to a specific page.
 * `selector` — element to spotlight; the tour polls for it (route changes
 *              and React renders can take a tick).
 * `before` — optional side-effect run on entry (e.g. fill a form field via
 *            a window CustomEvent that the page subscribes to).
 * `autoAdvance` — optional rule that auto-advances the step. Currently
 *                 only `{ on: 'route', match: <RegExp> }` is implemented;
 *                 the tour listens for location changes and moves on when
 *                 the new path matches.
 */
const STEPS = [
    {
        kind: 'modal',
        route: '/traders',
        kicker: 'Welcome',
        title: 'Find the right freight customers, faster.',
        body: 'FreightSource shows you UK importers and exporters who actually move freight. This two-minute tour walks you through your first search, how to save a lead, and how to bring colleagues in. You can skip any time.',
        primary: 'Start tour',
        secondary: 'Skip for now',
    },
    {
        kind: 'spotlight',
        route: '/traders',
        selector: '[data-onboard="search-postcode"]',
        kicker: 'Step 1 of 6 — Search',
        title: 'Search by postcode area.',
        body: 'Type a UK postcode area (e.g. BS for Bristol) or a full outcode (e.g. BS1). You can also filter by company name, radius, and HS commodity codes on the left.',
        primary: 'Show me how →',
    },
    {
        kind: 'spotlight',
        route: '/traders',
        selector: '[data-onboard="search-button"]',
        before: () => {
            window.dispatchEvent(
                new CustomEvent('onboarding:fill-search', {
                    detail: { postCode: 'BS1' },
                }),
            );
        },
        kicker: 'Step 2 of 6 — Run search',
        title: 'Hit Search to see traders in BS1.',
        body: "We've pre-filled BS1 (central Bristol) for you. Click Search to pull every importer and exporter we have on file for that outcode.",
        waitForUser: true,
        autoAdvance: { on: 'selector', match: '[data-onboard="trader-row"]' },
    },
    {
        kind: 'spotlight',
        route: '/traders',
        selector: '[data-onboard="trader-row"]',
        kicker: 'Step 3 of 6 — Open a trader',
        title: 'Click any row to open a trader.',
        body: 'Each row shows a company with its import and export activity. Open one to see the monthly chart, top HS codes, and quick links to Companies House.',
        waitForUser: true,
        autoAdvance: { on: 'route', match: /^\/traders\/[^/]+$/ },
    },
    {
        kind: 'spotlight',
        selector: '[data-onboard="save-trader"]',
        kicker: 'Step 4 of 6 — Save to pipeline',
        title: 'Save them to your pipeline.',
        body: 'Use the status dropdown to add a trader to your pipeline as Prospect, Client, Lapsed or Rejected. You can change the status later.',
        primary: 'Next',
    },
    {
        kind: 'spotlight',
        route: '/saved-traders/prospects',
        selector: '[data-onboard="nav-saved"]',
        kicker: 'Step 5 of 6 — Your pipeline',
        title: 'Everything you save lives here.',
        body: 'Saved traders is your kanban board. Drag between columns as deals move from Prospect to Client. You can get back here any time from the top nav.',
        primary: 'Next',
    },
    {
        kind: 'spotlight',
        route: '/profile',
        selector: '[data-onboard="add-member"]',
        kicker: 'Step 6 of 6 — Add your team',
        title: 'Bring colleagues into the same account.',
        body: "Add team members from Settings so the whole company shares one pipeline. If a colleague signs up separately, they'll create a duplicate organisation. You don't have to do this now — just know it's here.",
        primary: 'Next',
    },
    {
        kind: 'modal',
        kicker: 'All done',
        title: "You're set.",
        body: 'That\'s the loop: search, open, save, share. You can replay this tour any time from Settings → Replay tour.',
        primary: 'Finish',
    },
];

const PADDING = 8;
const CARD_GAP = 16;
const CARD_WIDTH = 320;
const POLL_INTERVAL_MS = 80;
const POLL_TIMEOUT_MS = 4000;

const OnboardingTour = () => {
    const { user, updateUser } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();

    const completed = Boolean(user?.onboarding_completed_at);
    const [active, setActive] = useState(false);
    const [stepIndex, setStepIndex] = useState(0);
    const [targetRect, setTargetRect] = useState(null);
    const startedFor = useRef(null);

    /* Open automatically the first time a logged-in user is seen without
     * a completion timestamp. `startedFor` guards against re-opening if
     * the user dismisses then the auth state refreshes. */
    useEffect(() => {
        if (!user) return;
        if (completed) return;
        if (startedFor.current === user.id) return;
        startedFor.current = user.id;
        setStepIndex(0);
        setActive(true);
    }, [user, completed]);

    /* External replay trigger — fired from the Profile page's "Replay tour"
     * button. */
    useEffect(() => {
        const onReplay = () => {
            setStepIndex(0);
            setActive(true);
        };
        window.addEventListener('onboarding:replay', onReplay);
        return () => window.removeEventListener('onboarding:replay', onReplay);
    }, []);

    const step = active ? STEPS[stepIndex] : null;

    /* When a step has a `route`, push the user there. We only navigate if
     * the current path doesn't already match. We also skip the navigation
     * when the step's autoAdvance route condition already matches — that
     * means the user is "ahead" of this step (e.g. already viewing a
     * trader during Step 3), so navigating backwards would be jarring and
     * then we'd immediately auto-advance anyway. */
    useEffect(() => {
        if (!step?.route) return;
        if (location.pathname === step.route) return;
        if (
            step.autoAdvance?.on === 'route' &&
            step.autoAdvance.match.test(location.pathname)
        ) {
            return;
        }
        navigate(step.route);
    }, [step, location.pathname, navigate]);

    /* Run side-effects when entering a step (e.g. pre-fill the search). */
    useEffect(() => {
        if (!step?.before) return;
        /* Wait a tick so the target page has mounted and subscribed to the
         * fill event. */
        const id = setTimeout(() => step.before(), 200);
        return () => clearTimeout(id);
    }, [step]);

    /* Poll for the spotlight target. The DOM may not be ready immediately
     * after a route change; React Router commits the new tree on the next
     * tick. */
    useEffect(() => {
        setTargetRect(null);
        if (!step || step.kind !== 'spotlight') return;

        let cancelled = false;
        const start = Date.now();

        let scrolled = false;
        const tick = () => {
            if (cancelled) return;
            const el = document.querySelector(step.selector);
            if (el) {
                const rect = el.getBoundingClientRect();
                if (rect.width > 0 && rect.height > 0) {
                    /* Scroll once so the target is comfortably in view —
                     * otherwise sidebar buttons near the fold end up hidden
                     * behind the floating card. */
                    if (!scrolled) {
                        scrolled = true;
                        el.scrollIntoView({
                            block: 'center',
                            inline: 'center',
                            behavior: 'instant',
                        });
                        setTimeout(tick, 60);
                        return;
                    }
                    setTargetRect(rect);
                    return;
                }
            }
            if (Date.now() - start < POLL_TIMEOUT_MS) {
                setTimeout(tick, POLL_INTERVAL_MS);
            } else {
                /* Give up gracefully — fall back to a centered card with no
                 * spotlight. */
                setTargetRect(null);
            }
        };
        tick();

        return () => {
            cancelled = true;
        };
    }, [step, location.pathname]);

    /* Recompute spotlight position on resize / scroll so it tracks the
     * underlying element. */
    useEffect(() => {
        if (!step || step.kind !== 'spotlight') return;

        const recompute = () => {
            const el = document.querySelector(step.selector);
            if (!el) return;
            const rect = el.getBoundingClientRect();
            if (rect.width > 0 && rect.height > 0) setTargetRect(rect);
        };

        window.addEventListener('resize', recompute);
        window.addEventListener('scroll', recompute, true);
        return () => {
            window.removeEventListener('resize', recompute);
            window.removeEventListener('scroll', recompute, true);
        };
    }, [step]);

    /* Auto-advance on route match. Also catches the "already there on
     * entry" case so we don't strand the user on a stale step when they're
     * resuming a session past the relevant action. */
    useEffect(() => {
        if (!step?.autoAdvance) return;
        if (step.autoAdvance.on !== 'route') return;
        if (step.autoAdvance.match.test(location.pathname)) {
            setStepIndex((i) => Math.min(i + 1, STEPS.length - 1));
        }
    }, [step, location.pathname]);

    /* Auto-advance when a target selector appears. Polls because the
     * element may be mounted async after a network round-trip. The first
     * tick also handles the "already on screen" case (cached results,
     * resume after refresh). */
    useEffect(() => {
        if (!step?.autoAdvance) return;
        if (step.autoAdvance.on !== 'selector') return;
        const sel = step.autoAdvance.match;
        let cancelled = false;
        const tick = () => {
            if (cancelled) return;
            if (document.querySelector(sel)) {
                setStepIndex((i) => Math.min(i + 1, STEPS.length - 1));
                return;
            }
            setTimeout(tick, 200);
        };
        tick();
        return () => {
            cancelled = true;
        };
    }, [step]);

    const persistCompletion = async () => {
        try {
            await api.post('profile/onboarding-complete');
            updateUser({ onboarding_completed_at: new Date().toISOString() });
        } catch (e) {
            console.error('Onboarding completion failed to persist:', e);
            /* Still close the tour locally — we don't want to trap the user. */
            updateUser({ onboarding_completed_at: new Date().toISOString() });
        }
    };

    const onSkip = async () => {
        setActive(false);
        await persistCompletion();
    };

    const onNext = async () => {
        if (stepIndex >= STEPS.length - 1) {
            setActive(false);
            await persistCompletion();
            return;
        }
        setStepIndex((i) => i + 1);
    };

    const totalSteps = useMemo(
        () => STEPS.filter((s) => s.kind === 'spotlight').length,
        [],
    );
    const currentSpotlightIndex = useMemo(() => {
        if (!step || step.kind !== 'spotlight') return -1;
        return STEPS.slice(0, stepIndex + 1).filter(
            (s) => s.kind === 'spotlight',
        ).length;
    }, [step, stepIndex]);

    if (!active || !step) return null;

    if (step.kind === 'modal') {
        const isFinal = stepIndex === STEPS.length - 1;
        return (
            <div className="onboard-root">
                <div className="onboard-modal-wrap" role="dialog" aria-modal="true">
                    <div className="onboard-modal">
                        <span className="onboard-kicker">
                            {isFinal && <span className="onboard-done-mark" />}
                            {step.kicker}
                        </span>
                        <h2>{step.title}</h2>
                        <p>{step.body}</p>
                        <div className="onboard-actions">
                            <span className="onboard-step-meta">
                                {isFinal ? '6 of 6' : 'Tour · 2 min'}
                            </span>
                            <div className="onboard-buttons">
                                {step.secondary && (
                                    <button
                                        type="button"
                                        className="onboard-btn onboard-btn-ghost"
                                        onClick={onSkip}
                                    >
                                        {step.secondary}
                                    </button>
                                )}
                                <button
                                    type="button"
                                    className="onboard-btn onboard-btn-primary"
                                    onClick={onNext}
                                >
                                    {step.primary || 'Next'}{' '}
                                    <span className="arrow">→</span>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    /* Spotlight step. */
    if (!targetRect) {
        /* Target not yet on screen — render dim only so we don't jump. */
        return (
            <div className="onboard-root">
                <div className="onboard-modal-wrap">
                    <div className="onboard-modal">
                        <span className="onboard-kicker">{step.kicker}</span>
                        <h2>{step.title}</h2>
                        <p>{step.body}</p>
                        <p
                            style={{
                                fontFamily: 'var(--font-mono)',
                                fontSize: 11,
                                color: 'var(--muted)',
                                textTransform: 'uppercase',
                                letterSpacing: '0.12em',
                            }}
                        >
                            Looking for the right spot…
                        </p>
                        <div className="onboard-actions">
                            <span className="onboard-step-meta">
                                {currentSpotlightIndex} of {totalSteps}
                            </span>
                            <div className="onboard-buttons">
                                <button
                                    type="button"
                                    className="onboard-btn onboard-btn-ghost"
                                    onClick={onSkip}
                                >
                                    Skip tour
                                </button>
                                <button
                                    type="button"
                                    className="onboard-btn onboard-btn-primary"
                                    onClick={onNext}
                                >
                                    {step.primary || 'Next'}{' '}
                                    <span className="arrow">→</span>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    /* Compute the four dim panels around the target. */
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const tx = Math.max(0, targetRect.left - PADDING);
    const ty = Math.max(0, targetRect.top - PADDING);
    const tw = Math.min(vw, targetRect.width + PADDING * 2);
    const th = Math.min(vh, targetRect.height + PADDING * 2);

    const ringStyle = {
        left: tx,
        top: ty,
        width: tw,
        height: th,
    };

    /* Place the card next to the target. Preference order:
     *   1. To the side that has the most free space (right of target, or
     *      left if the target hugs the right edge).
     *   2. Fall back to below / above only if side placement doesn't fit.
     *
     * Side placement matters because the most important spotlight targets
     * (postcode field, Search button, Add member button) sit in narrow
     * sidebars where a card below the target would overflow the viewport.
     */
    const CARD_HEIGHT = 260;
    const spaceRight = vw - (tx + tw);
    const spaceLeft = tx;
    const spaceBelow = vh - (ty + th);
    const spaceAbove = ty;

    let cardLeft;
    let cardTop;

    if (spaceRight >= CARD_WIDTH + 24) {
        cardLeft = tx + tw + CARD_GAP;
        cardTop = Math.max(12, Math.min(vh - CARD_HEIGHT - 12, ty));
    } else if (spaceLeft >= CARD_WIDTH + 24) {
        cardLeft = tx - CARD_WIDTH - CARD_GAP;
        cardTop = Math.max(12, Math.min(vh - CARD_HEIGHT - 12, ty));
    } else if (spaceBelow >= CARD_HEIGHT + 24) {
        cardTop = ty + th + CARD_GAP;
        cardLeft = Math.max(12, Math.min(vw - CARD_WIDTH - 12, tx));
    } else if (spaceAbove >= CARD_HEIGHT + 24) {
        cardTop = ty - CARD_HEIGHT - CARD_GAP;
        cardLeft = Math.max(12, Math.min(vw - CARD_WIDTH - 12, tx));
    } else {
        /* Nowhere to put it without overlap — center the card and rely on
         * the spotlight ring to mark the target. */
        cardLeft = Math.max(12, (vw - CARD_WIDTH) / 2);
        cardTop = Math.max(12, (vh - CARD_HEIGHT) / 2);
    }

    return (
        <div className="onboard-root">
            <div
                className="onboard-dim"
                style={{ left: 0, top: 0, width: vw, height: ty }}
            />
            <div
                className="onboard-dim"
                style={{ left: 0, top: ty + th, width: vw, height: Math.max(0, vh - ty - th) }}
            />
            <div
                className="onboard-dim"
                style={{ left: 0, top: ty, width: tx, height: th }}
            />
            <div
                className="onboard-dim"
                style={{
                    left: tx + tw,
                    top: ty,
                    width: Math.max(0, vw - tx - tw),
                    height: th,
                }}
            />
            <div className="onboard-spotlight-ring" style={ringStyle} />
            <div
                className="onboard-card"
                style={{ left: cardLeft, top: cardTop }}
                role="dialog"
                aria-modal="true"
            >
                <span className="onboard-kicker">{step.kicker}</span>
                <h2>{step.title}</h2>
                <p>{step.body}</p>
                <div className="onboard-dots">
                    {STEPS.map((s, i) =>
                        s.kind === 'spotlight' ? (
                            <span
                                key={i}
                                className={`onboard-dot ${
                                    i === stepIndex
                                        ? 'on'
                                        : i < stepIndex
                                          ? 'done'
                                          : ''
                                }`}
                            />
                        ) : null,
                    )}
                </div>
                <div className="onboard-actions">
                    <span className="onboard-step-meta">
                        {currentSpotlightIndex} of {totalSteps}
                    </span>
                    <div className="onboard-buttons">
                        <button
                            type="button"
                            className="onboard-btn onboard-btn-ghost"
                            onClick={onSkip}
                        >
                            Skip tour
                        </button>
                        {!step.waitForUser && (
                            <button
                                type="button"
                                className="onboard-btn onboard-btn-primary"
                                onClick={onNext}
                            >
                                {step.primary || 'Next'}{' '}
                                <span className="arrow">→</span>
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default OnboardingTour;
